/**
 * Billing service base — every billing service call goes through here so the
 * success/error handlers are invoked in the service layer (per the data-flow
 * rule), and screens only need a thin try/catch around the service.
 *
 * read():  GET; shows an error toast on failure, rethrows.
 * write(): POST/PUT/PATCH/DELETE; optional success toast; error toast; rethrows.
 */
import { Method } from 'axios';
import { billingApi } from '../../api/axiosInstance';
import { handleApiError } from '../../handlers/errorHandler';
import { showSuccess } from '../../handlers/successHandler';

export async function read<T = any>(
  url: string,
  errorTitle: string,
  params?: Record<string, unknown>,
  silent = false,
): Promise<T> {
  try {
    const { data } = await billingApi.get<T>(url, params ? { params } : undefined);
    return data;
  } catch (e) {
    if (!silent) handleApiError(e, errorTitle); // silent: caller handles expected misses (e.g. no FX rate)
    throw e;
  }
}

export async function write<T = any>(
  method: Extract<Method, 'post' | 'put' | 'patch' | 'delete'>,
  url: string,
  opts: {
    body?: unknown;
    errorTitle: string;
    successTitle?: string;
    successFallback?: string;
  },
): Promise<T> {
  try {
    const { data } = await billingApi.request<T>({ method, url, data: opts.body });
    if (opts.successTitle) showSuccess(data, opts.successTitle, opts.successFallback);
    return data;
  } catch (e) {
    handleApiError(e, opts.errorTitle);
    throw e;
  }
}
